/**
 * Practitioner Hub → Workshop Coupons.
 *
 * A practitioner discounts their own workshops: a code buyers type, or a plain
 * sale price with no code. Either can cover one workshop, several, or all of
 * theirs including ones they add later.
 *
 * The discount comes out of the creator's own share (and any collaborator split),
 * because the practitioner is the one choosing to run it.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Tag, X, AlertCircle, Ticket, Copy, Check } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const styles = `
  .wc-wrap { color: #f5f0e8; font-family: 'Nunito Sans', sans-serif; }
  .wc-section { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 24px; }
  .wc-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom: 18px; }
  .wc-title { font-family: 'Cormorant Garamond', serif; font-size: 22px; margin: 0; display:flex; align-items:center; gap:10px; }
  .wc-sub { font-size: 13px; color: rgba(245,240,232,0.55); margin: 6px 0 0; max-width: 60ch; line-height: 1.5; }
  .wc-list { display:flex; flex-direction:column; gap:10px; }
  .wc-row { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; padding:14px 16px; background: rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius:10px; }
  .wc-row-title { font-weight:600; font-size:15px; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .wc-row-meta { font-size:12px; color: rgba(245,240,232,0.55); margin-top:6px; display:flex; gap:14px; flex-wrap:wrap; }
  .wc-chip { background: rgba(201,168,76,0.30); color:#fbe9b8; padding:3px 10px; border-radius:6px; font-family:monospace; font-size:12px; letter-spacing:0.5px; }
  .wc-badge { font-size:10px; padding:3px 8px; border-radius:4px; text-transform:uppercase; letter-spacing:0.5px; }
  .wc-badge.sale { background: rgba(101,179,127,0.2); color:#9fdfb5; }
  .wc-badge.paused { background: rgba(120,120,120,0.2); color: rgba(245,240,232,0.5); }
  .wc-empty { padding:28px; text-align:center; color: rgba(245,240,232,0.5); font-style: italic; }
  .wc-form { background: rgba(0,0,0,0.25); border:1px solid rgba(255,255,255,0.1); border-radius:10px; padding:20px; margin-bottom:18px; }
  .wc-grid { display:grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap:14px; }
  .wc-field label { display:block; font-size:11px; color:rgba(245,240,232,0.7); margin-bottom:6px; text-transform:uppercase; letter-spacing:0.5px; }
  .wc-toggle-row { display:flex; gap:8px; flex-wrap:wrap; margin-top:8px; }
  .wc-pill { padding:6px 12px; border-radius:999px; font-size:13px; border:1px solid rgba(255,255,255,0.14); background:rgba(255,255,255,0.03); color:rgba(245,240,232,0.6); cursor:pointer; transition: all .15s ease; }
  .wc-pill:hover { color:#f5f0e8; }
  .wc-pill.on { background: rgba(201,168,76,0.4); border-color: #d9bc6a; color:#fff6dd; font-weight:600; box-shadow: 0 0 0 2px rgba(201,168,76,0.25); }
  .wc-foot { display:flex; justify-content:flex-end; gap:8px; margin-top:18px; }
  .wc-note { display:flex; gap:8px; font-size:12px; color: rgba(245,240,232,0.5); margin-top:14px; line-height:1.5; }
  .wc-input { width:100%; }
  /* The native date picker's icon is black on a dark field — invert it so it can be seen */
  .wc-wrap input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(1) opacity(0.65); cursor: pointer; }
`;

const blank = {
  title: '', kind: 'code', code: '', discount_percent: 15,
  target_all: false, workshop_ids: [], total_use_cap: '', expires_at: '',
};

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// A 422 `detail` arrives as an array of objects; passing it to a toast makes React throw.
const errMsg = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    // Pydantic prefixes its own messages with "Value error, " — not his language.
    const msgs = detail
      .map(d => (d?.msg || '').replace(/^Value error,\s*/i, ''))
      .filter(Boolean);
    return msgs.join('. ') || fallback;
  }
  if (typeof detail === 'string') return detail;
  return fallback;
};

function expiryLabel(iso) {
  if (!iso) return 'No end date';
  const d = new Date(iso);
  return `${d < new Date() ? 'Ended' : 'Ends'} ${d.toLocaleDateString()}`;
}

export default function WorkshopCouponsTab() {
  return (
    <div className="wc-wrap" data-testid="practitioner-coupons-tab">
      <style>{styles}</style>
      <MyCoachingCodes />
      <WorkshopCoupons />
    </div>
  );
}

// ── The practitioner's own coaching codes ──────────────────────────────
// They exist so he can see who brought each signup in, which only works if the
// practitioner can find and share their own code.

function MyCoachingCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/practitioner/my-coaching-codes`, { headers: headers() });
        setCodes(data || []);
      } catch {
        // A practitioner with no campaign running simply has nothing to show.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (code, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(code);
      toast.success('Copied — paste it anywhere');
      setTimeout(() => setCopied(''), 2500);
    } catch {
      toast.error('Could not copy, please select the code by hand');
    }
  };

  if (loading || codes.length === 0) return null;

  return (
    <div className="wc-section" style={{ marginBottom: 24 }} data-testid="my-coaching-codes">
      <div className="wc-head">
        <div>
          <h3 className="wc-title"><Ticket size={20} /> Your Coaching Codes</h3>
          <p className="wc-sub">
            Share these with anyone joining the coaching program. They apply when the program is
            paid in full, and every signup is credited to you.
          </p>
        </div>
      </div>

      <div className="wc-list">
        {codes.map(c => (
          <div key={c.code} className="wc-row" data-testid={`my-code-${c.code}`}>
            <div style={{ minWidth: 0 }}>
              <div className="wc-row-title">
                <span className="wc-chip" style={{ fontSize: 15, padding: '5px 12px' }}>{c.code}</span>
                <span style={{ color: '#e6c879', fontWeight: 600, fontSize: 13 }}>
                  {c.discount_percent}% off
                </span>
              </div>
              <div className="wc-row-meta">
                <span>{c.campaign}</span>
                <span>{(c.programs || []).join(', ')}</span>
                <span>{expiryLabel(c.expires_at)}</span>
                <span data-testid={`my-code-uses-${c.code}`}>
                  {c.redemptions} signup{c.redemptions === 1 ? '' : 's'} from your code
                </span>
                {c.pool_remaining !== null && c.pool_remaining !== undefined && (
                  <span style={{ color: c.pool_remaining <= 5 ? '#f0b7a4' : undefined }}>
                    {c.pool_remaining} left across all practitioners
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Button
                variant="outline" size="sm"
                onClick={() => copy(c.code, c.code)}
                className="border-white/25 text-white/90 hover:bg-white/10"
                data-testid={`copy-code-${c.code}`}
              >
                {copied === c.code ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                {copied === c.code ? 'Copied' : 'Copy code'}
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => copy(`${c.code}-msg`, c.share_text)}
                className="border-white/25 text-white/90 hover:bg-white/10"
                data-testid={`copy-message-${c.code}`}
              >
                {copied === `${c.code}-msg`
                  ? <Check size={14} className="mr-1" />
                  : <Copy size={14} className="mr-1" />}
                {copied === `${c.code}-msg` ? 'Copied' : 'Copy message'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkshopCoupons() {
  const [promos, setPromos] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/practitioner/workshop-promotions`, { headers: headers() });
      setPromos(data.promotions || []);
      setWorkshops(data.workshops || []);
    } catch {
      toast.error('Could not load your workshop coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const titleFor = (id) => workshops.find(w => w.id === id)?.title || 'Workshop';

  const save = async () => {
    if (!form.title.trim()) return toast.error('Give it a name');
    if (form.kind === 'code' && !form.code.trim()) return toast.error('Enter the code buyers will type');
    if (!form.target_all && form.workshop_ids.length === 0) {
      return toast.error('Choose which workshops this covers');
    }
    const percent = Number(form.discount_percent);
    if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) {
      return toast.error('The discount must be between 1% and 99%');
    }
    try {
      await axios.post(`${API}/practitioner/workshop-promotions`, {
        title: form.title.trim(),
        kind: form.kind,
        code: form.kind === 'code' ? form.code.trim().toUpperCase() : null,
        discount_percent: percent,
        target_all: form.target_all,
        workshop_ids: form.target_all ? [] : form.workshop_ids,
        total_use_cap: form.total_use_cap ? Number(form.total_use_cap) : null,
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
      }, { headers: headers() });
      toast.success(form.kind === 'code' ? 'Coupon code created' : 'Sale price is live');
      setForm(blank);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not save that'));
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.patch(`${API}/practitioner/workshop-promotions/${p.id}`,
        { active: !p.active }, { headers: headers() });
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not change this coupon'));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/practitioner/workshop-promotions/${p.id}`, { headers: headers() });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete this coupon'));
    }
  };

  const toggleWorkshop = (id) => setForm({
    ...form,
    workshop_ids: form.workshop_ids.includes(id)
      ? form.workshop_ids.filter(w => w !== id)
      : [...form.workshop_ids, id],
  });

  return (
    <div data-testid="workshop-coupons-tab">
      <div className="wc-section">
        <div className="wc-head">
          <div>
            <h3 className="wc-title"><Tag size={20} /> Workshop Coupons</h3>
            <p className="wc-sub">
              Discount your own workshops with a code buyers type at checkout, or a sale price
              that needs no code. The reduction comes out of your share of the sale.
            </p>
          </div>
          <Button
            onClick={() => setShowForm(v => !v)}
            className="bg-amber-700 hover:bg-amber-600 text-white shrink-0"
            data-testid="new-workshop-coupon-btn"
          >
            <Plus size={16} className="mr-1" /> New
          </Button>
        </div>

        {showForm && (
          <div className="wc-form" data-testid="workshop-coupon-form">
            <div className="wc-grid">
              <div className="wc-field">
                <label>Name</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Spring special"
                  className="wc-input bg-black/30 border-white/15 text-white"
                  data-testid="workshop-coupon-title-input"
                />
              </div>
              <div className="wc-field">
                <label>How it applies</label>
                <div className="wc-toggle-row">
                  {[['code', 'Code at checkout'], ['sale', 'Sale price']].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      className={`wc-pill ${form.kind === val ? 'on' : ''}`}
                      onClick={() => setForm({ ...form, kind: val })}
                      data-testid={`workshop-coupon-kind-${val}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              {form.kind === 'code' && (
                <div className="wc-field">
                  <label>Code</label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="SPRING15"
                    className="wc-input bg-black/30 border-white/15 text-white font-mono"
                    data-testid="workshop-coupon-code-input"
                  />
                </div>
              )}
              <div className="wc-field">
                <label>Discount %</label>
                <Input
                  type="number" min="1" max="99"
                  value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                  className="wc-input bg-black/30 border-white/15 text-white"
                  data-testid="workshop-coupon-percent-input"
                />
              </div>
              <div className="wc-field">
                <label>End date</label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="wc-input bg-black/30 border-white/15 text-white"
                  data-testid="workshop-coupon-expiry-input"
                />
              </div>
              <div className="wc-field">
                <label>Total redemptions</label>
                <Input
                  type="number" min="1"
                  value={form.total_use_cap}
                  onChange={(e) => setForm({ ...form, total_use_cap: e.target.value })}
                  placeholder="No limit"
                  className="wc-input bg-black/30 border-white/15 text-white"
                  data-testid="workshop-coupon-cap-input"
                />
              </div>
            </div>

            <div className="wc-field" style={{ marginTop: 16 }}>
              <label>Which workshops</label>
              <div className="wc-toggle-row">
                <button
                  type="button"
                  className={`wc-pill ${form.target_all ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, target_all: !form.target_all, workshop_ids: [] })}
                  data-testid="workshop-coupon-all-toggle"
                >
                  All of mine
                </button>
                {!form.target_all && workshops.map(w => (
                  <button
                    key={w.id}
                    type="button"
                    className={`wc-pill ${form.workshop_ids.includes(w.id) ? 'on' : ''}`}
                    onClick={() => toggleWorkshop(w.id)}
                    data-testid={`workshop-coupon-pick-${w.id}`}
                  >
                    {w.title}
                  </button>
                ))}
              </div>
              {!form.target_all && workshops.length === 0 && (
                <p className="wc-sub" style={{ marginTop: 10 }}>
                  You have no workshops yet — build one first and it will appear here.
                </p>
              )}
            </div>

            <div className="wc-note">
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                A code and a sale price never stack. If both apply to the same workshop, the buyer
                gets whichever is the larger saving.
              </span>
            </div>

            <div className="wc-foot">
              <Button variant="ghost" onClick={() => { setShowForm(false); setForm(blank); }} className="text-white/60">
                Cancel
              </Button>
              <Button onClick={save} className="bg-amber-700 hover:bg-amber-600" data-testid="workshop-coupon-save-btn">
                Save
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="wc-empty">Loading…</div>
        ) : promos.length === 0 ? (
          <div className="wc-empty" data-testid="workshop-coupons-empty">
            Nothing running yet.
          </div>
        ) : (
          <div className="wc-list">
            {promos.map(p => (
              <div key={p.id} className="wc-row" data-testid={`workshop-coupon-${p.id}`}>
                <div style={{ minWidth: 0 }}>
                  <div className="wc-row-title">
                    {p.title}
                    {p.kind === 'code'
                      ? <span className="wc-chip">{p.code}</span>
                      : <span className="wc-badge sale">Sale price</span>}
                    <span style={{ color: '#e6c879', fontSize: 13, fontWeight: 600 }}>
                      {p.discount_percent}% off
                    </span>
                    {!p.active && <span className="wc-badge paused">Paused</span>}
                  </div>
                  <div className="wc-row-meta">
                    <span>
                      {p.target_all
                        ? 'All my workshops'
                        : (p.target_ids || []).map(titleFor).join(', ')}
                    </span>
                    <span>{expiryLabel(p.expires_at)}</span>
                    <span>
                      {p.total_use_cap
                        ? `${p.uses_count || 0} of ${p.total_use_cap} redeemed`
                        : `${p.uses_count || 0} redeemed`}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <Switch
                    checked={!!p.active}
                    onCheckedChange={() => toggleActive(p)}
                    data-testid={`workshop-coupon-toggle-${p.id}`}
                  />
                  <Button
                    variant="outline" size="sm"
                    onClick={() => remove(p)}
                    className="border-rose-900 text-rose-200 hover:bg-rose-950"
                    data-testid={`workshop-coupon-delete-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
